import React, { useEffect, useState } from 'react'
import { Button, Card, Col, Form, Input, InputNumber, List, Select, Space, Typography } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons'
import CustomRow from '../../../reusable-components/CustomRow'
import { usePhysicalActivity } from '../hooks/usePhysicalActivity'

const { Title, Text } = Typography
const { Option } = Select

const PhysicalActivityManagement = ({ physicalActivities, setPhysicalActivities }) => {
  const [form] = Form.useForm()
  const [selectedActivity, setSelectedActivity] = useState(null)
  const { physicalActivitiesOptions, isLoading } = usePhysicalActivity()

  useEffect(() => {
    form.resetFields()
  }, [selectedActivity, form])

  const handleAddActivity = (values) => {
    const newActivity = {
      id: Date.now(),
      name: values.name,
      type: values.type,
      description: values.description,
      duration: values.duration,
      frequency: values.frequency,
      intensity: values.intensity,
      goals: values.goals
    }

    setPhysicalActivities([...physicalActivities, newActivity])
    form.resetFields()
  }

  const handleDeleteActivity = (id) => {
    setPhysicalActivities(physicalActivities.filter((item) => item.id !== id))
    if (selectedActivity && selectedActivity.id === id) {
      setSelectedActivity(null)
    }
  }

  const handleEditActivity = () => {
    if (!selectedActivity) return

    const values = form.getFieldsValue()
    const updatedActivities = physicalActivities.map((item) => {
      if (item.id === selectedActivity.id) {
        return {
          ...item,
          name: values.name,
          type: values.type,
          description: values.description,
          duration: values.duration,
          frequency: values.frequency,
          intensity: values.intensity,
          goals: values.goals
        }
      }
      return item
    })

    setPhysicalActivities(updatedActivities)
    setSelectedActivity(null)
  }

  return (
    <CustomRow>
      <Col span={24}>
        <Title level={4}>Exercise & Physical Activity</Title>
        <Text>Configure exercise routines, physical activities, and movement plans for the wellness program.</Text>
      </Col>

      <Col span={12} style={{ paddingRight: 12 }}>
        <Card title='Physical Activities' loading={isLoading}>
          <List
            dataSource={physicalActivities}
            renderItem={(item) => (
              <List.Item
                key={item.id}
                actions={[
                  <Button
                    type='text'
                    danger
                    onClick={() => handleDeleteActivity(item.id)}
                    icon={<FontAwesomeIcon icon={faTrash} />}
                  />
                ]}
              >
                <List.Item.Meta
                  title={item.name}
                  description={`${item.type} - ${item.frequency} - ${item.intensity} intensity`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedActivity(item)}
                />
              </List.Item>
            )}
            footer={
              <Button
                type='dashed'
                block
                onClick={() => setSelectedActivity(null)}
                icon={<FontAwesomeIcon icon={faPlus} />}
              >
                Add New Activity
              </Button>
            }
          />
        </Card>
      </Col>

      <Col span={12}>
        <Card title={selectedActivity ? 'Edit Physical Activity' : 'Add Physical Activity'}>
          <Form
            form={form}
            layout='vertical'
            onFinish={selectedActivity ? handleEditActivity : handleAddActivity}
            initialValues={selectedActivity || {}}
          >
            <Form.Item name='name' label='Activity Name' rules={[{ required: true, message: 'Please enter a name' }]}>
              <Input placeholder='e.g., Morning Walk' />
            </Form.Item>

            <Form.Item
              name='type'
              label='Activity Type'
              rules={[{ required: true, message: 'Please select an activity type' }]}
            >
              <Select placeholder='Select activity type'>
                {physicalActivitiesOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name='description' label='Description'>
              <Input.TextArea rows={2} placeholder='Describe the activity...' />
            </Form.Item>

            <Form.Item
              name='duration'
              label='Duration (minutes)'
              rules={[{ required: true, message: 'Please specify duration' }]}
            >
              <InputNumber min={1} max={240} />
            </Form.Item>

            <Form.Item
              name='frequency'
              label='Frequency'
              rules={[{ required: true, message: 'Please select a frequency' }]}
            >
              <Select placeholder='Select frequency'>
                <Option value='daily'>Daily</Option>
                <Option value='2-3 times per week'>2-3 times per week</Option>
                <Option value='weekly'>Weekly</Option>
                <Option value='bi-weekly'>Bi-Weekly</Option>
                <Option value='monthly'>Monthly</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name='intensity'
              label='Intensity'
              rules={[{ required: true, message: 'Please select intensity' }]}
            >
              <Select placeholder='Select intensity level'>
                <Option value='low'>Low</Option>
                <Option value='moderate'>Moderate</Option>
                <Option value='high'>High</Option>
                <Option value='variable'>Variable</Option>
              </Select>
            </Form.Item>

            <Form.Item name='goals' label='Activity Goals'>
              <Select mode='multiple' placeholder='Select activity goals'>
                <Option value='cardiovascular'>Cardiovascular Health</Option>
                <Option value='strength'>Strength</Option>
                <Option value='flexibility'>Flexibility</Option>
                <Option value='balance'>Balance</Option>
                <Option value='endurance'>Endurance</Option>
                <Option value='stress-reduction'>Stress Reduction</Option>
                <Option value='weight-management'>Weight Management</Option>
              </Select>
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type='primary' htmlType='submit'>
                  {selectedActivity ? 'Update' : 'Add'} Activity
                </Button>
                {selectedActivity && <Button onClick={() => setSelectedActivity(null)}>Cancel</Button>}
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </Col>
    </CustomRow>
  )
}

export default PhysicalActivityManagement
